import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDown, Check, Cpu } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { getSavedKeyStatus } from '@/services/settingsKeys';

interface ModelEntry {
  provider: string;
  model: string;
  label: string;
  icon?: React.ReactNode;
}

interface ModelSelectorProps {
  compact?: boolean;
  onModelChange?: (provider: string, model: string) => void;
}

// v3.6.71: 改用全称 (不再用 DS/QW/CLD 简称)
const PROVIDER_LABELS: Record<string, string> = {
  deepseek: 'DeepSeek',
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google',
  qwen: 'Qwen',
  moonshot: 'Moonshot',
  zhipu: 'Zhipu',
};

// v3.6.71: 改用全称 (不再用 V3/R1/V4 Flash 等简称)
const MODEL_SHORT_NAMES: Record<string, string> = {
  'deepseek-chat': 'DeepSeek-V3',
  'deepseek-reasoner': 'DeepSeek-R1',
  'deepseek-v4-flash': 'DeepSeek-V4-Flash',
  'deepseek-v4-pro': 'DeepSeek-V4-Pro',
  'qwen3.8-max-preview': 'Qwen3.8-Max',
  'qwen3.7-max': 'Qwen3.7-Max',
  'qwen3.7-plus': 'Qwen3.7-Plus',
  'qwen3.6-flash': 'Qwen3.6-Flash',
  'qwen3.6-plus': 'Qwen3.6-Plus',
  'qwen3.5-plus': 'Qwen3.5-Plus',
  'qwen3.5-flash': 'Qwen3.5-Flash',
  'qwen3-coder-plus': 'Qwen3-Coder+',
  'qwen3-coder-flash': 'Qwen3-Coder-F',
  'qwen-long': 'Qwen-Long',
  'qwen3.5:9b': 'Qwen3.5-9B',
  'qwen2.5:7b': 'Qwen2.5-7B',
  'qwen-max': 'Qwen-Max',
  'qwen-plus': 'Qwen-Plus',
  'qwen-turbo': 'Qwen-Turbo',
  'qwq-plus': 'QwQ-Plus',
  'qvl-2.5': 'QVl-2.5',
  'gpt-4o': 'GPT-4o',
  'gpt-4o-mini': 'GPT-4o-Mini',
  'gpt-4-turbo': 'GPT-4-Turbo',
  'gpt-4.1': 'GPT-4.1',
  'gpt-4.1-mini': 'GPT-4.1-Mini',
  'gpt-4.1-nano': 'GPT-4.1-Nano',
  'o3': 'O3',
  'o3-mini': 'O3-Mini',
  'o4-mini': 'O4-Mini',
  'claude-3.5-sonnet': 'Claude-3.5-Sonnet',
  'claude-3-opus': 'Claude-3-Opus',
  'claude-sonnet-4-20250514': 'Claude-Sonnet-4',
  'claude-opus-4-20250514': 'Claude-Opus-4',
  'gemini-1.5-pro': 'Gemini-1.5-Pro',
  'gemini-2.0-flash': 'Gemini-2.0-Flash',
  'gemini-2.5-flash': 'Gemini-2.5-Flash',
  'gemini-2.5-pro': 'Gemini-2.5-Pro',
};

/** Server-side env/key name per provider (used to detect backend-configured keys) */
const PROVIDER_SERVER_KEY: Record<string, string> = {
  deepseek: 'DEEPSEEK_API_KEY',
  openai: 'OPENAI_API_KEY',
  anthropic: 'ANTHROPIC_API_KEY',
  google: 'GEMINI_API_KEY',
  qwen: 'DASHSCOPE_API_KEY',
  moonshot: 'KIMI_API_KEY',
  zhipu: 'GLM_API_KEY',
};

/** Built-in model catalog per provider (shown when API key is configured) */
const PROVIDER_MODEL_CATALOG: Record<string, string[]> = {
  deepseek: [
    'deepseek-chat',
    'deepseek-reasoner',
    'deepseek-v4-flash',
    'deepseek-v4-pro',
  ],
  openai: [
    'gpt-4.1',
    'gpt-4.1-mini',
    'gpt-4.1-nano',
    'gpt-4o',
    'gpt-4o-mini',
    'gpt-4-turbo',
    'o3',
    'o3-mini',
    'o4-mini',
  ],
  anthropic: [
    'claude-opus-4-20250514',
    'claude-sonnet-4-20250514',
    'claude-3.5-sonnet',
    'claude-3-opus',
  ],
  google: [
    'gemini-2.5-pro',
    'gemini-2.5-flash',
    'gemini-2.0-flash',
    'gemini-1.5-pro',
  ],
  qwen: [
    'qwen3.8-max-preview',
    'qwen3.7-max',
    'qwen3.7-plus',
    'qwen3.6-flash',
    'qwen3.6-plus',
    'qwen3.5-plus',
    'qwen3.5-flash',
    'qwen3-coder-plus',
    'qwen-long',
    'qwen-plus',
    'qwen-turbo',
    'qwq-plus',
  ],
  moonshot: [
    'moonshot-v1-8k',
    'moonshot-v1-32k',
    'moonshot-v1-128k',
  ],
  zhipu: [
    'glm-4-plus',
    'glm-4-air',
    'glm-4-flash',
    'glm-4-long',
  ],
};

function getProviderShortName(provider: string): string {
  return PROVIDER_LABELS[provider] || provider.slice(0, 3).toUpperCase();
}

// v3.6.71: 没找到简写时返回原名 (不再截断 8 字符)
function getModelShortName(model: string): string {
  return MODEL_SHORT_NAMES[model] || model.split('/').pop() || model;
}

// v3.6.72: 只返回模型名 (dropdown 已有 provider 标签,工具栏按钮只需简洁的模型名)
function getDisplayLabel(provider: string, model: string): string {
  return getModelShortName(model);
}

export function ModelSelector({ compact = false, onModelChange }: ModelSelectorProps) {
  const { aiConfig, updateAIConfig } = useApp();
  const [isOpen, setIsOpen] = useState(false);
  const [availableModels, setAvailableModels] = useState<ModelEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load available models from localStorage and server prefs
  useEffect(() => {
    const loadModels = async () => {
      try {
        const savedModels = (() => {
          try { return JSON.parse(localStorage.getItem('xf_llm_models') || '{}'); } catch { return {}; }
        })();

        // Try fetching server prefs
        const res = await fetch('/api/preferences/llm', { credentials: 'include' });
        const serverPrefs = await res.json().catch(() => ({}));

        // Detect backend-configured API keys (so models show even if not in localStorage)
        let keyStatus: Record<string, boolean> = {};
        try { keyStatus = await getSavedKeyStatus(); } catch { keyStatus = {}; }

        const entries: ModelEntry[] = [];
        const serverModels = serverPrefs.models || {};
        const savedModelsObj = savedModels || {};

        // Track which provider+model combos we've already added
        const seenKeys = new Set<string>();
        const seenProviders = new Set<string>();

        // 1. Add models from server prefs / saved single-model entries
        for (const [provider, model] of Object.entries(serverModels)) {
          if (model && typeof model === 'string') {
            const key = `${provider}|${model}`;
            if (!seenKeys.has(key)) {
              entries.push({ provider, model, label: getDisplayLabel(provider, model) });
              seenKeys.add(key);
            }
          }
        }

        // 2. For each provider with an API key configured in localStorage,
        //    use the built-in catalog to show ALL available models
        const catalogProviders = Object.keys(PROVIDER_MODEL_CATALOG);
        for (const provider of catalogProviders) {
          const localKey = localStorage.getItem(`xf_${provider}_key`);
          const serverKeyName = PROVIDER_SERVER_KEY[provider];
          const serverKeyConfigured = serverKeyName ? !!keyStatus[serverKeyName] : false;
          if (!localKey && !serverKeyConfigured) continue;

          const catalogModels = PROVIDER_MODEL_CATALOG[provider] || [];
          const savedModel = savedModelsObj[provider];

          for (const modelName of catalogModels) {
            const key = `${provider}|${modelName}`;
            if (seenKeys.has(key)) continue;
            entries.push({
              provider,
              model: modelName,
              label: getDisplayLabel(provider, modelName),
            });
            seenKeys.add(key);
          }

          // Also add the user's saved model if it's not in our catalog
          if (savedModel && typeof savedModel === 'string' && !catalogModels.includes(savedModel)) {
            const sKey = `${provider}|${savedModel}`;
            if (!seenKeys.has(sKey)) {
              entries.push({ provider, model: savedModel, label: getDisplayLabel(provider, savedModel) });
              seenKeys.add(sKey);
            }
          }
        }

        // Always add current model if not in list
        if (!entries.find(e => e.provider === aiConfig.provider && e.model === aiConfig.model)) {
          entries.unshift({
            provider: aiConfig.provider,
            model: aiConfig.model,
            label: getDisplayLabel(aiConfig.provider, aiConfig.model),
          });
        }

        setAvailableModels(entries);
      } catch {
        // Fallback: just show current model
        setAvailableModels([{
          provider: aiConfig.provider,
          model: aiConfig.model,
          label: getDisplayLabel(aiConfig.provider, aiConfig.model),
        }]);
      } finally {
        setLoading(false);
      }
    };
    loadModels();
  }, [aiConfig.provider, aiConfig.model]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = async (entry: ModelEntry) => {
    updateAIConfig({ provider: entry.provider, model: entry.model });

    // Persist to server
    const allModels = (() => {
      try { return JSON.parse(localStorage.getItem('xf_llm_models') || '{}'); } catch { return {}; }
    })();
    allModels[entry.provider] = entry.model;
    localStorage.setItem('xf_llm_models', JSON.stringify(allModels));

    fetch('/api/preferences/llm', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider: entry.provider, models: allModels }),
      credentials: 'include',
    }).catch(() => {});

    onModelChange?.(entry.provider, entry.model);
    setIsOpen(false);
  };

  const currentLabel = getDisplayLabel(aiConfig.provider, aiConfig.model);
  const isActive = (entry: ModelEntry) =>
    entry.provider === aiConfig.provider && entry.model === aiConfig.model;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.04] px-2.5 py-1.5 text-xs font-bold text-white/70 transition-all hover:border-white/20 hover:bg-white/[0.08] hover:text-white ${
          compact ? 'px-2 py-1' : ''
        }`}
        title={aiConfig.provider + ' / ' + aiConfig.model}
      >
        <Cpu size={12} className="text-white/40" />
        <span className="max-w-[130px] truncate">{loading ? '...' : currentLabel}</span>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown size={12} className="text-white/30" />
        </motion.div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute bottom-full mb-2 right-0 z-50 min-w-[260px] rounded-xl border border-white/10 bg-zinc-900/95 p-1 shadow-2xl backdrop-blur-xl"
          >
            {availableModels.length === 0 ? (
              <div className="px-3 py-4 text-xs text-white/35 text-center">No models configured</div>
            ) : (
              availableModels.map((entry) => (
                <button
                  key={`${entry.provider}-${entry.model}`}
                  type="button"
                  onClick={() => handleSelect(entry)}
                  className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs transition-colors ${
                    isActive(entry)
                      ? 'bg-white/10 text-white'
                      : 'text-white/55 hover:bg-white/5 hover:text-white/80'
                  }`}
                >
                  <span className="flex h-5 min-w-[60px] items-center justify-center rounded bg-white/10 px-1.5 text-[10px] font-black uppercase shrink-0">
                    {getProviderShortName(entry.provider)}
                  </span>
                  <span className="flex-1 text-left font-medium">{entry.label}</span>
                  {isActive(entry) && <Check size={14} className="text-celestial-saturn" />}
                </button>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
